import { of, catchError, forkJoin } from 'rxjs';
import { ChatHistoryHelper } from './chat-history.helper';

/**
 * ChatAnswerLoadingHelper
 *
 * Extracted from ChatbotexpandComponent. Holds the chat-answer loading cluster:
 * `loadAnswersIncrementally`, `lazyFetchBlockAnswer`, `populateBlockInsights`, and
 * `resolvePendingMessage`. These load analytical answers for chat preview blocks (in
 * parallel around the target, lazily on scroll, or by resolving a pending message) and
 * seed per-block/insights caches.
 *
 * Follows the existing `comp: any` helper pattern — the component keeps thin wrapper
 * methods with identical signatures that delegate here, so all call sites (including
 * cross-calls between these methods and template bindings like onChatScroll ->
 * lazyFetchBlockAnswer) remain unchanged and behavior is preserved (verbatim `this.` ->
 * `comp.` transcription).
 */
export class ChatAnswerLoadingHelper {
  static loadAnswersIncrementally(comp: any, chat: any, messageId: string): void {
    if (comp.isPreFetchingAnswers) return;
    comp.isPreFetchingAnswers = true;

    // 1. Map chatHistory to chatPreviewBlocks if not already done
    comp.chatPreviewBlocks = (chat.ChatHistory || []).map((h: any) =>
      ChatHistoryHelper.createChatPreviewBlock(h, comp.defaultQuestionAnswer, comp.DefaultWeight, comp.DefaultBase, comp.selectedWeight)
    );

    // Find the index of the selected messageId
    const targetIdx = comp.chatPreviewBlocks.findIndex((b: any) => b.messageId?.toLowerCase() === messageId.toLowerCase());
    if (targetIdx === -1) {
      console.warn(`[INCREMENTAL-LOAD] Target messageId ${messageId} not found in history!`);
      comp.isPreFetchingAnswers = false;
      comp._loaderService.hideLoader();
      return;
    }

    // Collect IDs of target, one above (targetIdx - 1), and one below (targetIdx + 1)
    const indicesToLoad = [targetIdx, targetIdx - 1, targetIdx + 1].filter(idx => idx >= 0 && idx < comp.chatPreviewBlocks.length);

    const blocksToLoad = indicesToLoad.map(idx => comp.chatPreviewBlocks[idx]).filter((b: any) => b && b.messageId && !b.fullAnswer);

    if (blocksToLoad.length === 0) {
      comp.isPreFetchingAnswers = false;
      comp.resolvePendingMessage(messageId);
      return;
    }

    // Call apiService.getChatResponse for each in parallel
    const obsList = blocksToLoad.map((block: any) => {
      return comp.apiService.getChatResponse(block.messageId!).pipe(
        catchError((err: any) => {
          console.error('[INCREMENTAL-LOAD] Error fetching block:', block.messageId, err);
          return of(null);
        })
      );
    });

    forkJoin(obsList).subscribe({
      next: (results: any[]) => {
        results.forEach((res, index) => {
          if (!res) return;
          const block = blocksToLoad[index];
          try {
            const apiResponse = JSON.parse(res);
            if (apiResponse.HasValidData && apiResponse.Data) {
              const parsedData = JSON.parse(apiResponse.Data);
              const historyItem = Array.isArray(parsedData) ? parsedData[0] : parsedData;
              let answerData = historyItem?.Answer;
              if (typeof answerData === 'string') {
                try {
                  answerData = JSON.parse(answerData);
                } catch (e) {
                  console.error('[INCREMENTAL-LOAD] Failed to parse answer string:', e);
                }
              }
              block.fullAnswer = answerData;
              const parsed = comp.parseChatResponse(answerData);
              block.parsedData = parsed;
              const parsedMcode = parsed.member_responses?.final_mcode || parsed.AgentPayload?.mcode || parsed.GetMcode?.mcode || 0;
              if (!block.userSelectedMcode) {
                block.mcode = parsedMcode;
              }
              block.isBrandAnalysis = (block.mcode === 2);
              block.is_sigtesting = (block.mcode === 2 || block.mcode === 3);
              const __rfInc = comp.computeStudyRibbonFlags();
              block.is_confidenceLevel = [...__rfInc.confidenceLevels];
              block.is_TransposeBtn = __rfInc.transposeAvailable;
              block.analyticalData = parsed;
              
              const mr = parsed.member_responses;
              if (mr) {
                const tryParseConf = (v: any) => {
                  if (!v) return null;
                  try { return typeof v.getConfiguration === 'string' ? JSON.parse(v.getConfiguration) : v.getConfiguration; } catch { return null; }
                };
                if (mr.crosstab) {
                  block.crosstabPayload = tryParseConf(mr.crosstab)?.crosstab_payload ||
                    (typeof mr.crosstab.getConfiguration === 'object' ? mr.crosstab.getConfiguration?.crosstab_payload : null);
                }
                if (mr.brand_analysis || mr.brandanalysis) {
                  const ba = mr.brand_analysis || mr.brandanalysis;
                  block.brandAnalysisPayload = tryParseConf(ba)?.brand_analysis_payload || tryParseConf(ba)?.brand_anlysis_payload ||
                    (typeof ba.getConfiguration === 'object' ? ba.getConfiguration?.brand_analysis_payload : null);
                }
                if (mr.snapshot) {
                  block.snapshotPayload = tryParseConf(mr.snapshot)?.snapshot_payload ||
                    (typeof mr.snapshot.getSnapshotOutput === 'object' ? mr.snapshot.getSnapshotOutput : null);
                }
              }

              const rawData = parsed.AgentPayload || parsed.GetMcode;
              if (rawData) {
                block.availableViews = comp.identifyAvailableViews(rawData, parsed.member_responses);
                if (!block.userSelectedMcode) {
                  block.activeView = block.availableViews.find((v: any) => v.mcode === block.mcode) || block.availableViews[0];
                } else {
                  block.activeView = block.availableViews.find((v: any) => v.mcode === block.mcode) || block.activeView;
                }

                // Set default response payload for the UI to render immediately
                if (block.activeView && parsed.member_responses) {
                  const result = parsed.member_responses[block.activeView.key] || parsed.member_responses[block.activeView.key.replace('_', '')];
                  if (result) {
                    (parsed as any).response = result.response || result;
                  }
                }
              }
              comp.populateBlockInsights(block, parsed);
              comp.updateChartDisablingFlags(block);
              ChatAnswerLoadingHelper.updateBlockWeight(comp, block, parsed);
            }
          } catch (e) {
            console.error('[INCREMENTAL-LOAD] Parse error for block:', block.messageId, e);
          }
        });

        // Force array reference update
        comp.chatPreviewBlocks = [...comp.chatPreviewBlocks];

        // Resolve the pending/selected message
        comp.resolvePendingMessage(messageId);
        comp.isPreFetchingAnswers = false;
      },
      error: (err: any) => {
        console.error('[INCREMENTAL-LOAD] Parallel load failed:', err);
        comp.isPreFetchingAnswers = false;
        comp.isScrollingToTarget = false;
        comp._loaderService.hideLoader();
      }
    });
  }

  static lazyFetchBlockAnswer(comp: any, block: any): void {
    if (!block || !block.messageId || block.fullAnswer || comp.inFlightLazyFetches.has(block.messageId)) {
      return;
    }

    comp.inFlightLazyFetches.add(block.messageId);

    comp.apiService.getChatResponse(block.messageId).subscribe({
      next: (res: string) => {
        try {
          const apiResponse = JSON.parse(res);
          if (apiResponse.HasValidData && apiResponse.Data) {
            const parsedData = JSON.parse(apiResponse.Data);
            const historyItem = Array.isArray(parsedData) ? parsedData[0] : parsedData;
            let answerData = historyItem?.Answer;
            if (typeof answerData === 'string') {
              try {
                answerData = JSON.parse(answerData);
              } catch (e) {
                console.error('[LAZY-FETCH] Failed to parse:', e);
              }
            }
            block.fullAnswer = answerData;
            const parsed = comp.parseChatResponse(answerData);
            block.parsedData = parsed;
            const parsedMcodeLazy = parsed.member_responses?.final_mcode || parsed.AgentPayload?.mcode || parsed.GetMcode?.mcode || 0;
            if (!block.userSelectedMcode) {
              block.mcode = parsedMcodeLazy;
            }
            block.isBrandAnalysis = (block.mcode === 2);
            block.is_sigtesting = (block.mcode === 2 || block.mcode === 3);
            const __rfLazy = comp.computeStudyRibbonFlags();
            block.is_confidenceLevel = [...__rfLazy.confidenceLevels];
            block.is_TransposeBtn = __rfLazy.transposeAvailable;
            block.analyticalData = parsed;

              const mrLazy = parsed.member_responses;
              if (mrLazy) {
                const tryParseConfL = (v: any) => {
                  if (!v) return null;
                  try { return typeof v.getConfiguration === 'string' ? JSON.parse(v.getConfiguration) : v.getConfiguration; } catch { return null; }
                };
                if (mrLazy.crosstab) {
                  block.crosstabPayload = tryParseConfL(mrLazy.crosstab)?.crosstab_payload ||
                    (typeof mrLazy.crosstab.getConfiguration === 'object' ? mrLazy.crosstab.getConfiguration?.crosstab_payload : null);
                }
                if (mrLazy.brand_analysis || mrLazy.brandanalysis) {
                  const baL = mrLazy.brand_analysis || mrLazy.brandanalysis;
                  block.brandAnalysisPayload = tryParseConfL(baL)?.brand_analysis_payload || tryParseConfL(baL)?.brand_anlysis_payload ||
                    (typeof baL.getConfiguration === 'object' ? baL.getConfiguration?.brand_analysis_payload : null);
                }
                if (mrLazy.snapshot) {
                  block.snapshotPayload = tryParseConfL(mrLazy.snapshot)?.snapshot_payload ||
                    (typeof mrLazy.snapshot.getSnapshotOutput === 'object' ? mrLazy.snapshot.getSnapshotOutput : null);
                }
              }

            const rawData = parsed.AgentPayload || parsed.GetMcode;
            if (rawData) {
              block.availableViews = comp.identifyAvailableViews(rawData, parsed.member_responses);
              if (!block.userSelectedMcode) {
                block.activeView = block.availableViews.find((v: any) => v.mcode === block.mcode) || block.availableViews[0];
              } else {
                block.activeView = block.availableViews.find((v: any) => v.mcode === block.mcode) || block.activeView;
              }

              // Set default response payload for the UI to render immediately
              if (block.activeView && parsed.member_responses) {
                const result = parsed.member_responses[block.activeView.key] || parsed.member_responses[block.activeView.key.replace('_', '')];
                if (result) {
                  (parsed as any).response = result.response || result;
                }
              }
            }
            comp.populateBlockInsights(block, parsed);
            comp.updateChartDisablingFlags(block);
            ChatAnswerLoadingHelper.updateBlockWeight(comp, block, parsed);
          }
        } catch (e) {
          console.error('[LAZY-FETCH] Parse error:', e);
        }
        comp.inFlightLazyFetches.delete(block.messageId!);
      },
      error: (err: any) => {
        console.error(`[LAZY-FETCH] ❌ Failed to lazy load message: ${block.messageId}`, err);
        comp.inFlightLazyFetches.delete(block.messageId!);
      }
    });
  }

  static populateBlockInsights(comp: any, block: any, parsed: any): void {
    const pAny = parsed as any;

    // 1. Normal Insights Extraction
    if (pAny.Insights && Array.isArray(pAny.Insights)) {
      block.insights = pAny.Insights;
    } else if (pAny.getSnapshotInsights) {
      const gsi = pAny.getSnapshotInsights;
      if (gsi.Insights && Array.isArray(gsi.Insights)) {
        block.insights = gsi.Insights;
      } else if (typeof gsi === 'string') {
        block.insights = comp.formatInsightsIntoPoints(gsi);
      } else if (gsi.Value) {
        block.insights = comp.formatInsightsIntoPoints(gsi.Value);
      } else if (gsi.insights?.Value) {
        block.insights = comp.formatInsightsIntoPoints(gsi.insights.Value);
      }
    } else if (pAny.getInsights) {
      if (Array.isArray(pAny.getInsights)) {
        block.insights = pAny.getInsights;
      } else if (typeof pAny.getInsights === 'string') {
        block.insights = comp.formatInsightsIntoPoints(pAny.getInsights);
      } else if (pAny.getInsights.Insights) {
        block.insights = pAny.getInsights.Insights;
      }
    } else if (pAny.getSnapshotOutput?.Insights) {
      block.insights = pAny.getSnapshotOutput.Insights;
    }

    // 2. Trend Insights Extraction
    if (pAny.getSnapshotInsightsTrend) {
      const gsit = pAny.getSnapshotInsightsTrend;
      if (gsit.Insights && Array.isArray(gsit.Insights)) {
        block.insightsTrend = gsit.Insights;
      } else if (typeof gsit === 'string') {
        block.insightsTrend = comp.formatInsightsIntoPoints(gsit);
      } else if (gsit.Value) {
        block.insightsTrend = comp.formatInsightsIntoPoints(gsit.Value);
      } else if (gsit.insights?.Value) {
        block.insightsTrend = comp.formatInsightsIntoPoints(gsit.insights.Value);
      }
    }

    // 3. Seed multiViewInsightsCache for all modules from history
    if (block.messageId && parsed.member_responses) {
      if (!comp.multiViewInsightsCache[block.messageId]) {
        comp.multiViewInsightsCache[block.messageId] = {};
      }

      const res = parsed.member_responses;
      const tryParse = (val: any) => {
        if (typeof val === 'string') {
          try {
            const trimmed = val.trim();
            if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
              return JSON.parse(trimmed);
            }
          } catch (e) { }
        }
        return val;
      };

      const mapModuleInsights = (modData: any, code: number) => {
        if (!modData) return;
        const qnaText = tryParse(modData.getQnA) || '';

        const existingCache = comp.multiViewInsightsCache[block.messageId]?.[code];
        const hasExistingData = existingCache && ((existingCache.insightsVal && existingCache.insightsVal.length > 0) || (existingCache.qnaText && existingCache.qnaText.trim() !== ''));

        if (!hasExistingData) {
          if (code === 1) {
            const std = tryParse(modData.getSnapshotInsights) || '';
            const trd = tryParse(modData.getSnapshotInsights_Trend) || '';
            const combined = `${std}[[[SPLIT]]]${trd}`;
            comp.multiViewInsightsCache[block.messageId][code] = {
              insightsVal: comp.formatInsightsIntoPoints(combined),
              qnaText: qnaText,
              standard_insights: std,
              trend_insights: trd,
              rawInsights: combined
            };
          } else {
            const raw = tryParse(modData.getInsights) || '';
            comp.multiViewInsightsCache[block.messageId][code] = {
              insightsVal: comp.formatInsightsIntoPoints(raw),
              qnaText: qnaText,
              rawInsights: raw
            };
          }
        }
      };

      mapModuleInsights(res.snapshot, 1);
      mapModuleInsights(res.brand_analysis || res.brandanalysis, 2);
      mapModuleInsights(res.crosstab, 3);
    }
  }

  static resolvePendingMessage(comp: any, messageId: string): void {
    if (comp.pendingMessageId) {
      const matchedBlock = comp.chatPreviewBlocks.find((b: any) => b.messageId?.toLowerCase() === comp.pendingMessageId?.toLowerCase());
      if (matchedBlock && matchedBlock.fullAnswer) {
        comp.sendInputMessage(matchedBlock.fullAnswer, true, matchedBlock);
        comp.scrollToMessage(comp.pendingMessageId, 'start');
        comp._loaderService.hideLoader();
        comp.pendingMessageId = null;
      } else {
        comp._loaderService.hideLoader();
        comp.pendingMessageId = null;
      }
    }
  }

  static updateBlockWeight(comp: any, block: any, parsed: any): void {
    let rawWeight: any = null;
    
    const tryParse = (v: any) => {
      if (!v) return null;
      try { return typeof v === 'string' ? JSON.parse(v) : v; } catch { return null; }
    };

    if (block.crosstabPayload) {
      rawWeight = block.crosstabPayload.SelectedWeight || block.crosstabPayload.weight || block.crosstabPayload.Weight;
    } else if (block.brandAnalysisPayload) {
      rawWeight = block.brandAnalysisPayload.SelectedWeight || block.brandAnalysisPayload.weight || block.brandAnalysisPayload.Weight;
    } else if (block.snapshotPayload) {
      rawWeight = block.snapshotPayload.SelectedWeight || block.snapshotPayload.weight || block.snapshotPayload.Weight;
    }

    if (!rawWeight && parsed.AgentPayload) {
      rawWeight = parsed.AgentPayload.weight || parsed.AgentPayload.Weight;
    }
    if (!rawWeight && parsed.GetMcode) {
      rawWeight = parsed.GetMcode.weight || parsed.GetMcode.Weight;
    }
    if (!rawWeight && parsed.member_responses?.crosstab) {
      const ctConf = tryParse(parsed.member_responses.crosstab.getConfiguration);
      if (ctConf?.crosstab_payload) {
         rawWeight = ctConf.crosstab_payload.SelectedWeight || ctConf.crosstab_payload.weight || ctConf.crosstab_payload.Weight;
      }
    }
    if (!rawWeight && parsed.member_responses?.brand_analysis) {
      const baConf = tryParse(parsed.member_responses.brand_analysis.getConfiguration);
      if (baConf?.brand_analysis_payload) {
         rawWeight = baConf.brand_analysis_payload.SelectedWeight || baConf.brand_analysis_payload.weight || baConf.brand_analysis_payload.Weight;
      }
    }
    if (!rawWeight && parsed.member_responses?.brandanalysis) {
      const baConf2 = tryParse(parsed.member_responses.brandanalysis.getConfiguration);
      if (baConf2?.brand_analysis_payload) {
         rawWeight = baConf2.brand_analysis_payload.SelectedWeight || baConf2.brand_analysis_payload.weight || baConf2.brand_analysis_payload.Weight;
      }
    }
    
    if (!rawWeight) {
      rawWeight = block.selectedWeight;
    }

    let fallbackSelected = rawWeight;
    if ((block.mcode === 2 || block.mcode === 3) && (!rawWeight || rawWeight === 'Weight' || rawWeight === 'Weight 1')) {
      let defaultFallback = comp.WeightDropDownList?.find((w: any) => w.Label !== 'Unweighted' && w.Label !== 'Boost Factor');
      if (defaultFallback) {
        fallbackSelected = defaultFallback.responseCode;
      }
    }

    rawWeight = rawWeight || fallbackSelected;

    if (comp.WeightDropDownList && comp.WeightDropDownList.length > 0) {
      const matchedWeight = comp.WeightDropDownList.find((w: any) => 
        w.responseCode === rawWeight || 
        w.Label === rawWeight || 
        w.tooltip === rawWeight
      );
      if (matchedWeight) {
        block.selectedWeight = matchedWeight.responseCode;
        block.DefaultWeight = matchedWeight.Label;
      } else {
        block.DefaultWeight = rawWeight === 'Unweighted' ? 'Total Population' : rawWeight;
        block.selectedWeight = rawWeight;
      }
    } else {
      block.DefaultWeight = rawWeight === 'Unweighted' ? 'Total Population' : rawWeight;
      block.selectedWeight = rawWeight;
    }
  }
}
